/* eslint new-cap: ["error", { "capIsNewExceptions": ["DropTarget", "DragSource"] }] */

import React from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';

import styled from 'styled-components';

import { DragSource, DropTarget } from 'react-dnd';
import { colors, DraggableItemTypes } from '../../constants';

// the function for what happens when you drag a specified row
const dragSource = {
  beginDrag(props) {
    return { index: props.index };
  },
};

// the function for what happens when you try to drop a specified row
const dropTarget = {
  /**
   * Called when an item is hovered over a component
   *
   * @param props - the props of the component being dragged.
   * @param component - the item which has another item hovering above it
   *
   * @see reactdnd.DropTarget#hover
   * */
  hover(props, monitor, component) {
    /**
     * @see reactdnd.DragSourceMonitor
     * */
    const dragIndex = monitor.getItem().index;
    const hoverIndex = props.index;

    // don't try to replace the item with itself
    if (dragIndex === hoverIndex) {
      return;
    }

    // this component should be agnostic of the underlying element but still retain the
    // ability to get the root DOM element so silence eslint from calling the "WAAA-mbulance"
    const hoverRowHitBox = findDOMNode(component).getBoundingClientRect(); // eslint-disable-line react/no-find-dom-node
    const hoverRowMiddleY = (hoverRowHitBox.bottom - hoverRowHitBox.top) / 2;

    // the location of the pointer from the top of the list
    const mouseOffset = monitor.getClientOffset();

    // get offset from the top of the item we are hovering over
    const mouseOffsetFromHoverRow = mouseOffset.y - hoverRowHitBox.top;

    const isDraggingUpwards = dragIndex > hoverIndex;
    const isDraggingDownwards = dragIndex < hoverIndex;
    const isCursorAboveHalfwayOfRow = mouseOffsetFromHoverRow < hoverRowMiddleY;
    const isCursorBelowHalfwayOfRow = mouseOffsetFromHoverRow > hoverRowMiddleY;

    if (isDraggingUpwards && isCursorBelowHalfwayOfRow) {
      return;
    }

    if (isDraggingDownwards && isCursorAboveHalfwayOfRow) {
      return;
    }

    props.reorder(dragIndex, hoverIndex);

    // Note: we're mutating the monitor item here!
    // Generally it's better to avoid mutations,
    // but it's necessary here for perf. reasons.
    //
    // This is because the hover event gets fired too quickly and could call props.reorder()
    // multiple times causing the component to swap the same elements multiple times in a
    // fraction of a second.
    //
    // Debouncing is not an option as it causes de-syncronization of internal state for
    // react-dnd
    monitor.getItem().index = hoverIndex;
  },
};

// the function that tells react-dnd what props should be injected into our component
const dragCollector = (connector, monitor) => {
  return {
    connectDragSource: connector.dragSource(),
    isDragging: monitor.isDragging(),
  };
};

const dropCollector = (connector) => {
  return {
    connectDropTarget: connector.dropTarget(),
  };
};

class ReorderableRow extends React.Component {
  static propTypes = {
    connectDragSource: PropTypes.func.isRequired, // injected by collector
    connectDropTarget: PropTypes.func.isRequired, // injected by collector
    isDragging: PropTypes.bool.isRequired, // injected by collector

    index: PropTypes.number.isRequired,
    reorder: PropTypes.func.isRequired,
  };

  render() {
    const {
      connectDragSource,
      connectDropTarget,
      style,
      isDragging,
      className,
    } = this.props;
    const dragStyle = isDragging
      ? { border: '2px dashed ' + colors.mediumGreenhouseGreen }
      : {};

    return connectDragSource(
      connectDropTarget(
        <li style={Object.assign(style || {}, dragStyle)} className={className}>
          <DraggableItem>{this.props.children}</DraggableItem>
        </li>
      )
    );
  }
}

const dragSourceComponent = DragSource(
  DraggableItemTypes.GENERIC_ROW,
  dragSource,
  dragCollector
);
const dropTargetComponent = DropTarget(
  DraggableItemTypes.GENERIC_ROW,
  dropTarget,
  dropCollector
);

export default dropTargetComponent(dragSourceComponent(ReorderableRow));

const DraggableItem = styled.div`
  display: block;

  &:hover {
    cursor: move;
  }
`;
